/**
 * Renews the session after a request was refused with `token`; resolves to the token to retry
 * with, or to undefined when the session cannot be renewed (the browser is then on its way to the
 * sign-in page, or offline).
 */
export type RenewSession = (failedToken: string) => Promise<string | undefined>;

/**
 * Sends a request with the session's access token. A 401 means the token expired (a computer that
 * slept, a tab left open past the token's 15 minutes): the session is renewed and the request sent
 * once more with the new token. The response of that second attempt is final, so a token the API
 * keeps refusing never loops.
 */
export async function authorizedFetch(
  url: string,
  init: RequestInit,
  token: string | undefined,
  renew: RenewSession,
): Promise<Response> {
  const send = (bearer: string | undefined) => {
    const headers = new Headers(init.headers);
    if (bearer) headers.set('Authorization', `Bearer ${bearer}`);
    return fetch(url, { ...init, headers });
  };
  const response = await send(token);
  if (response.status !== 401 || !token) return response;
  const renewed = await renew(token);
  return renewed ? send(renewed) : response;
}
